// artwork.js — reaching the artwork that frontend/scripts/copy_logos.py publishes out of
// frontend/shapes/ into public/.
//
// The originals live in shapes/, filed by kind (team logos, competition logos, data providers).
// The script normalises each filename to a slug and writes a manifest.json of slug -> filename
// beside the files. The manifest is the point: a crest can be an .svg or a .png depending on what
// the club supplied, and pages must not carry their own copy of which is which. That map went out
// of date the first time a club's artwork was resupplied in the other format, and the page then
// asked for a file that was no longer there.
//
// So: no page hard-codes an extension. It asks the manifest for the filename and builds the URL.

import { useEffect, useState } from 'react';

const BASE = process.env.PUBLIC_URL || '';

export const CLUB_LOGOS = 'club-logos';
export const COMPETITIONS = 'competitions';
export const DATA_PROVIDERS = 'data-providers';

// One fetch per folder for the life of the page, shared by every component that asks. The
// manifests are a few hundred bytes and never change while the app is running.
const pending = new Map();

export function loadManifest(folder) {
  if (!pending.has(folder)) {
    // `no-cache`: revalidated on every page load. The artwork is re-filed from time to time
    // (copy_logos.py mirrors the folders, deleting what moved), and a manifest the browser kept
    // from before points at files that are gone — every crest turns to initials until a hard
    // refresh. Revalidating costs a 304 on a few hundred bytes.
    pending.set(folder, fetch(`${BASE}/${folder}/manifest.json`, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : {}))
      .then((m) => m || {})
      .catch(() => ({})));
  }
  return pending.get(folder);
}

/** One folder's manifest inside a component. `null` until it has arrived. */
export function useManifest(folder) {
  const [manifest, setManifest] = useState(null);
  useEffect(() => {
    let alive = true;
    loadManifest(folder).then((m) => { if (alive) setManifest(m); });
    return () => { alive = false; };
  }, [folder]);
  return manifest;
}

/** The URL of one published file, e.g. artworkUrl(CLUB_LOGOS, 'al-ettifaq.svg'). */
export function artworkUrl(folder, file) {
  return file ? `${BASE}/${folder}/${file}` : null;
}

export default loadManifest;
